package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.AudioError
import fr.manyverse.app.domain.model.AudioData
import fr.manyverse.app.domain.model.Sound
import fr.manyverse.app.domain.model.SoundTag
import fr.manyverse.app.domain.model.SoundType
import fr.manyverse.app.domain.repository.SoundRepository
import fr.manyverse.app.infrastructure.audio.extractAudioDurationSeconds
import fr.manyverse.app.infrastructure.datastore.SoundMetadata
import fr.manyverse.app.infrastructure.datastore.SoundMetadataDataStore
import kotlinx.coroutines.CancellationException
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

private const val TAG = "LocalSoundRepository"

/**
 * Local implementation of SoundRepository.
 * Stores user-uploaded sounds on the device using LocalAudioStorageRepository and SoundMetadataDataStore.
 * Combines default sounds from InMemorySoundRepository with locally stored user sounds.
 */
class LocalSoundRepository(
    private val inMemoryRepository: InMemorySoundRepository,
    private val audioStorageRepository: LocalAudioStorageRepository,
    private val soundMetadataDataStore: SoundMetadataDataStore
) : SoundRepository {

    override fun getAllSounds(): List<Sound> = inMemoryRepository.getAllSounds()

    override fun getSoundsByTags(tags: Set<SoundTag>): List<Sound> =
        inMemoryRepository.getSoundsByTags(tags)

    override fun getSoundById(id: String): Sound? = inMemoryRepository.getSoundById(id)

    override fun getSoundsByUniverse(universeId: String): List<Sound> =
        inMemoryRepository.getSoundsByUniverse(universeId)

    override suspend fun getSoundsByUniverseAndUser(
        universeId: String,
        userId: String
    ): Result<List<Sound>> {
        return try {
            // Get default sounds for this universe
            val defaultSounds = inMemoryRepository.getSoundsByUniverse(universeId)

            // Get user-uploaded sounds from local storage
            val userSoundsMetadata = soundMetadataDataStore.getSoundsByUserAndUniverse(
                userId = userId,
                universeId = universeId
            )
            val userSounds: List<Sound> = userSoundsMetadata.map { it.toDomain() }

            // Combine default sounds with user sounds
            Result.success(defaultSounds + userSounds)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            AppLogger.error(TAG, "Failed to load user sounds for universe=$universeId", e)
            Result.failure(AudioError("Failed to load sounds", e))
        }
    }

    @OptIn(ExperimentalUuidApi::class)
    override suspend fun uploadSound(
        userId: String,
        universeId: String,
        name: String,
        type: SoundType,
        tags: Set<SoundTag>,
        audioData: AudioData
    ): Result<Sound> {
        return try {
            val soundId = Uuid.random().toString()

            // Upload audio file to local storage
            val storagePath = audioStorageRepository.uploadAudio(userId, soundId, audioData)
                .getOrThrow()

            // Create sound metadata
            val durationSeconds = extractDurationOrNull(audioData)
            val metadata = SoundMetadata(
                id = soundId,
                userId = userId,
                universeId = universeId,
                name = name,
                type = type.name,
                tags = tags.map { it.name },
                fileName = audioData.fileName,
                localPath = storagePath,
                durationSeconds = durationSeconds
            )

            // Save metadata to local storage
            soundMetadataDataStore.saveSoundMetadata(metadata)
                .getOrThrow()

            Result.success(metadata.toDomain())
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            AppLogger.error(TAG, "Failed to save sound (${audioData.mimeType}, ${audioData.bytes.size} bytes)", e)
            Result.failure(AudioError("Failed to save the sound", e))
        }
    }

    override suspend fun deleteSound(soundId: String, userId: String): Result<Unit> {
        return try {
            // Delete from local file storage
            audioStorageRepository.deleteAudio(userId, soundId)

            // Delete metadata
            soundMetadataDataStore.deleteSoundMetadata(soundId)
                .getOrThrow()

            Result.success(Unit)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            AppLogger.error(TAG, "Failed to delete sound $soundId", e)
            Result.failure(AudioError("Failed to delete the sound", e))
        }
    }

    override suspend fun getAudioBytes(sound: Sound): Result<ByteArray> {
        return if (sound.isUserUploaded && sound.storagePath != null) {
            // User-uploaded sound: read from local storage. Can fail if the browser evicted
            // IndexedDB or the file was removed outside the app
            audioStorageRepository.downloadAudio(sound.storagePath)
                .onFailure { AppLogger.error(TAG, "Failed to read sound ${sound.id} from local storage", it) }
        } else {
            // Default sound: not supported here, handled by audio player
            Result.failure(UnsupportedOperationException("Default sounds are loaded from resources"))
        }
    }

    /**
     * The duration is display-only: failing to read it must not prevent adding the sound.
     * `null` is expected for some formats and platforms (always on web); only an exception,
     * raised by a malformed file, is worth a trace.
     */
    private fun extractDurationOrNull(audioData: AudioData): Int? = try {
        extractAudioDurationSeconds(audioData.bytes)
    } catch (e: Exception) {
        AppLogger.warn(TAG, "Audio duration unreadable (${audioData.mimeType}): ${e::class.simpleName}")
        null
    }
}
